import React, { useState, useEffect, useCallback } from 'react';
import { useParams, useNavigate, Link } from 'react-router-dom';
import { mtproAPI, tenantsAPI } from '../services/api';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { Skeleton } from '../components/ui/skeleton';
import { toast } from 'sonner';
import {
  ArrowLeft, Users, Globe, Building2, Server, Plus, Pencil,
  Trash2, ExternalLink, Loader2, Check, X
} from 'lucide-react';

const NAVY   = '#1e3a5f';
const ORANGE = '#ea580c';
const PURPLE = 'hsl(265 89% 45%)';

function UserCountEditor({ value, onSave, disabled }) {
  const [editing, setEditing] = useState(false);
  const [val, setVal]         = useState(String(value || 0));
  const [saving, setSaving]   = useState(false);

  const save = async () => {
    setSaving(true);
    await onSave(parseInt(val) || 0);
    setSaving(false);
    setEditing(false);
  };

  if (!editing) return (
    <button onClick={() => { setVal(String(value || 0)); setEditing(true); }} disabled={disabled}
      className="flex items-center gap-1 font-bold tabular-nums text-sm group"
      style={{ color: ORANGE }}>
      {value || 0} <span className="text-xs font-normal" style={{ color: 'hsl(var(--muted-fg))' }}>users</span>
      {!disabled && <Pencil size={10} className="opacity-0 group-hover:opacity-60 transition-opacity ml-1" />}
    </button>
  );
  return (
    <div className="flex items-center gap-1.5">
      <Input type="number" min="0" value={val} onChange={e => setVal(e.target.value)}
        className="h-7 w-20 text-sm tabular-nums" autoFocus
        onKeyDown={e => { if (e.key === 'Enter') save(); if (e.key === 'Escape') setEditing(false); }} />
      <button onClick={save} disabled={saving} className="p-1 rounded" style={{ color: '#16a34a' }}>
        {saving ? <Loader2 size={13} className="animate-spin" /> : <Check size={13} />}
      </button>
      <button onClick={() => setEditing(false)} className="p-1 rounded" style={{ color: 'hsl(var(--muted-fg))' }}>
        <X size={13} />
      </button>
    </div>
  );
}

export default function MtproSystemDetailPage() {
  const { id }        = useParams();
  const navigate      = useNavigate();
  const [system, setSystem]           = useState(null);
  const [tenants, setTenants]         = useState([]);
  const [loading, setLoading]         = useState(true);
  const [partnerTenants, setPartnerTenants] = useState([]);  // unlinked MTPro tenants for this partner
  const [showAddPanel, setShowAddPanel]     = useState(false);
  const [addMode, setAddMode]               = useState('existing'); // 'existing' | 'new'
  const [selectedTenantId, setSelectedTenantId] = useState('');
  const [newTenantName, setNewTenantName]        = useState('');
  const [newTenantUsers, setNewTenantUsers]      = useState('');
  const [adding, setAdding]           = useState(false);
  const [editingName, setEditingName] = useState(false);
  const [nameVal, setNameVal]         = useState('');

  const load = useCallback(async () => {
    try {
      const r = await mtproAPI.getSystem(id);
      setSystem(r.data);
      setTenants(r.data.tenants || []);
      setNameVal(r.data.name || '');
      // Load available unlinked MTPro tenants for this partner
      if (r.data.partner_id) {
        tenantsAPI.list({ partner_id: r.data.partner_id, tenant_type: 'mtpro', page_size: 200 })
          .then(tr => {
            const linked = new Set((r.data.tenants || []).map(t => t.id));
            setPartnerTenants((tr.data?.items || []).filter(t => !linked.has(t.id)));
          }).catch(() => {});
      }
    } catch { toast.error('Failed to load MTPro system'); }
    finally { setLoading(false); }
  }, [id]);

  useEffect(() => { load(); }, [load]);

  const saveName = async () => {
    if (!nameVal.trim()) return;
    try {
      await mtproAPI.updateSystem(id, { name: nameVal.trim() });
      setSystem(s => ({ ...s, name: nameVal.trim() }));
      setEditingName(false);
      toast.success('System name updated');
    } catch { toast.error('Failed to update name'); }
  };

  const handleLinkTenant = async () => {
    if (addMode === 'existing' && !selectedTenantId) {
      toast.error('Please select a tenant'); return;
    }
    if (addMode === 'new' && !newTenantName.trim()) {
      toast.error('Tenant name is required'); return;
    }
    setAdding(true);
    try {
      let tenantId = selectedTenantId;
      if (addMode === 'new') {
        const r = await tenantsAPI.create({
          company_name: newTenantName.trim(),
          tenant_type: 'mtpro',
          num_users: parseInt(newTenantUsers) || 0,
          partner_id: system?.partner_id || null,
        });
        tenantId = r.data?.id;
      }
      await mtproAPI.linkTenant(id, {
        tenant_id: tenantId,
        num_users: addMode === 'new' ? parseInt(newTenantUsers) || 0 : undefined,
      });
      toast.success('Tenant linked to system');
      setShowAddPanel(false);
      setSelectedTenantId('');
      setNewTenantName('');
      setNewTenantUsers('');
      setAddMode('existing');
      load();
    } catch (e) { toast.error(e.response?.data?.detail || 'Failed to add tenant'); }
    finally { setAdding(false); }
  };

  const handleUnlink = async (tenantId, tenantName) => {
    if (!window.confirm(`Remove ${tenantName} from this system?`)) return;
    try {
      await mtproAPI.unlinkTenant(id, tenantId);
      toast.success(`${tenantName} removed from system`);
      load();
    } catch { toast.error('Failed to remove tenant'); }
  };

  const handleUpdateUsers = async (tenantId, num_users) => {
    await mtproAPI.updateTenant(id, tenantId, { num_users });
    load();
  };

  if (loading) return (
    <div className="max-w-4xl mx-auto space-y-4">
      <Skeleton className="h-8 w-48" />
      <Skeleton className="h-40 w-full rounded-2xl" />
      <Skeleton className="h-64 w-full rounded-2xl" />
    </div>
  );

  if (!system) return <p>System not found.</p>;

  const totalAllocated = tenants.reduce((s, t) => s + (t.num_users || 0), 0);
  const systemUsers    = system.num_users || 0;
  const remaining      = systemUsers - totalAllocated;

  return (
    <div className="max-w-4xl mx-auto space-y-6">
      {/* Back */}
      <div className="flex items-center gap-2">
        <Button variant="ghost" size="sm" onClick={() => navigate('/mtpro-systems')}>
          <ArrowLeft size={15} />
        </Button>
        <span className="text-sm font-medium" style={{ color: 'hsl(var(--muted-fg))' }}>MTPro Systems</span>
        <span style={{ color: 'hsl(var(--muted-fg))' }}>/</span>
        <span className="text-sm font-semibold" style={{ color: NAVY }}>{system.name}</span>
      </div>

      {/* System header card */}
      <div className="rounded-2xl border bg-white overflow-hidden shadow-sm">
        <div className="px-6 py-4" style={{ background: 'linear-gradient(135deg, #1e3a5f 0%, #2d5a8e 100%)' }}>
          <div className="flex items-start justify-between gap-4 flex-wrap">
            <div>
              {editingName ? (
                <div className="flex items-center gap-2">
                  <Input value={nameVal} onChange={e => setNameVal(e.target.value)}
                    className="h-8 text-white font-bold bg-white/20 border-white/30 text-sm"
                    autoFocus onKeyDown={e => { if (e.key === 'Enter') saveName(); if (e.key === 'Escape') setEditingName(false); }} />
                  <button onClick={saveName} className="p-1 rounded" style={{ color: '#86efac' }}><Check size={16} /></button>
                  <button onClick={() => setEditingName(false)} className="p-1 rounded" style={{ color: 'rgba(255,255,255,0.5)' }}><X size={16} /></button>
                </div>
              ) : (
                <div className="flex items-center gap-2 group">
                  <h1 className="text-xl font-black text-white">{system.name}</h1>
                  <button onClick={() => setEditingName(true)}
                    className="opacity-0 group-hover:opacity-70 transition-opacity"
                    style={{ color: 'white' }}><Pencil size={13} /></button>
                </div>
              )}
              <div className="flex items-center gap-4 mt-2 flex-wrap text-sm" style={{ color: 'rgba(255,255,255,0.7)' }}>
                {system.partner_name && <span className="flex items-center gap-1"><Building2 size={12} /> {system.partner_name}</span>}
                {system.region_name  && <span className="flex items-center gap-1"><Globe size={12} /> {system.region_name}</span>}
                <span className="flex items-center gap-1"><Users size={12} /> {systemUsers} users</span>
                <span className="flex items-center gap-1"><Server size={12} /> {system.currency || ''}</span>
              </div>
            </div>
            <div className="flex items-center gap-3 flex-shrink-0">
              <span className="text-xs font-bold px-2.5 py-1 rounded-full"
                style={system.status === 'active'
                  ? { background: '#dcfce7', color: '#15803d' }
                  : { background: '#f1f5f9', color: '#64748b' }}>
                {system.status === 'active' ? '● Active' : '○ Inactive'}
              </span>
              {system.origin_quote_number && (
                <Link to={`/quotes?search=${system.origin_quote_number}`}
                  className="flex items-center gap-1 text-xs font-mono px-2 py-1 rounded-lg"
                  style={{ background: 'rgba(255,255,255,0.15)', color: 'rgba(255,255,255,0.8)' }}>
                  <ExternalLink size={11} /> {system.origin_quote_number}
                </Link>
              )}
            </div>
          </div>
        </div>

        {/* User allocation bar */}
        <div className="px-6 py-3 border-b" style={{ background: '#f8fafc' }}>
          <div className="flex items-center gap-4 flex-wrap text-sm">
            <div>
              <span className="font-bold tabular-nums" style={{ color: NAVY }}>{totalAllocated}</span>
              <span style={{ color: 'hsl(var(--muted-fg))' }}> / {systemUsers} users allocated</span>
            </div>
            {tenants.length > 0 && (
              <div className="flex-1 max-w-xs">
                <div className="h-1.5 rounded-full overflow-hidden" style={{ background: '#e2e8f0' }}>
                  <div className="h-full rounded-full transition-all"
                    style={{ width: `${Math.min(100, systemUsers > 0 ? (totalAllocated/systemUsers)*100 : 0)}%`,
                             background: remaining < 0 ? '#dc2626' : remaining === 0 ? '#16a34a' : PURPLE }} />
                </div>
              </div>
            )}
            {remaining > 0 && <span className="text-xs px-2 py-0.5 rounded-full" style={{ background: '#fef3c7', color: '#92400e' }}>{remaining} unallocated</span>}
            {remaining < 0 && <span className="text-xs px-2 py-0.5 rounded-full" style={{ background: '#fee2e2', color: '#991b1b' }}>{Math.abs(remaining)} over-allocated</span>}
            {remaining === 0 && totalAllocated > 0 && <span className="text-xs px-2 py-0.5 rounded-full" style={{ background: '#dcfce7', color: '#166534' }}>Fully allocated</span>}
          </div>
        </div>
      </div>

      {/* Tenants section */}
      <div className="rounded-2xl border bg-white overflow-hidden shadow-sm">
        <div className="px-6 py-4 flex items-center justify-between" style={{ borderBottom: '1px solid hsl(var(--border))' }}>
          <div>
            <h2 className="font-bold text-base" style={{ color: NAVY }}>Tenants on this System</h2>
            <p className="text-xs mt-0.5" style={{ color: 'hsl(var(--muted-fg))' }}>
              {tenants.length} tenant{tenants.length !== 1 ? 's' : ''} · click user count to edit
            </p>
          </div>
          <Button size="sm" onClick={() => setShowAddPanel(p => !p)} className="gap-1.5"
            style={{ background: PURPLE, color: 'white' }}>
            <Plus size={13} /> Add Tenant
          </Button>
        </div>

        {/* Add tenant panel */}
        {showAddPanel && (
          <div className="px-6 py-4 space-y-3" style={{ background: 'hsl(265 89% 98%)', borderBottom: '1px solid hsl(265 89% 85%)' }}>
            <p className="text-sm font-semibold" style={{ color: PURPLE }}>Link a tenant to this system</p>
            {/* Mode toggle */}
            <div className="flex gap-2">
              {[['existing','Select existing tenant'],['new','Create new tenant']].map(([m,l]) => (
                <button key={m} onClick={() => setAddMode(m)}
                  className="px-3 py-1.5 rounded-lg text-xs font-medium border transition-colors"
                  style={addMode === m
                    ? { background: PURPLE, color: 'white', borderColor: PURPLE }
                    : { background: 'white', color: 'hsl(var(--muted-fg))', borderColor: 'hsl(var(--border))' }}>
                  {l}
                </button>
              ))}
            </div>

            {addMode === 'existing' ? (
              <div className="space-y-3">
                <div className="space-y-1.5">
                  <label className="text-xs font-semibold" style={{ color: NAVY }}>Select Tenant</label>
                  <Select value={selectedTenantId} onValueChange={setSelectedTenantId}>
                    <SelectTrigger className="w-full" data-testid="tenant-select">
                      <SelectValue placeholder="- Select MTPro tenant -" />
                    </SelectTrigger>
                    <SelectContent>
                      {partnerTenants.length === 0
                        ? <SelectItem value="__none__" disabled>No available MTPro tenants for this partner</SelectItem>
                        : partnerTenants.map(t => (
                            <SelectItem key={t.id} value={t.id}>
                              {t.company_name}{t.num_users ? ` - ${t.num_users} users` : ''}
                            </SelectItem>
                          ))
                      }
                    </SelectContent>
                  </Select>
                  {partnerTenants.length === 0 && (
                    <p className="text-xs" style={{ color: 'hsl(var(--muted-fg))' }}>
                      Create an MTPro tenant on the Tenants page first, then return here to link it.
                    </p>
                  )}
                </div>
                <div className="flex items-center gap-2">
                  <Button onClick={handleLinkTenant} disabled={adding || !selectedTenantId}
                    style={{ background: PURPLE, color: 'white' }} className="gap-1.5">
                    {adding ? <Loader2 size={13} className="animate-spin" /> : <Plus size={13} />} Link Tenant
                  </Button>
                  <Button variant="outline" onClick={() => setShowAddPanel(false)}>Cancel</Button>
                </div>
              </div>
            ) : (
              <div className="space-y-3">
                <div className="grid grid-cols-2 gap-3">
                  <div className="space-y-1.5">
                    <label className="text-xs font-semibold" style={{ color: NAVY }}>Tenant / Company Name *</label>
                    <Input value={newTenantName} onChange={e => setNewTenantName(e.target.value)}
                      placeholder="e.g. Acme Corporation" />
                  </div>
                  <div className="space-y-1.5">
                    <label className="text-xs font-semibold" style={{ color: NAVY }}>User Count</label>
                    <Input type="number" min="0" value={newTenantUsers}
                      onChange={e => setNewTenantUsers(e.target.value)} placeholder="0" />
                  </div>
                </div>
                <div className="flex items-center gap-2">
                  <Button onClick={handleLinkTenant} disabled={adding || !newTenantName.trim()}
                    style={{ background: PURPLE, color: 'white' }} className="gap-1.5">
                    {adding ? <Loader2 size={13} className="animate-spin" /> : <Plus size={13} />} Create &amp; Link
                  </Button>
                  <Button variant="outline" onClick={() => setShowAddPanel(false)}>Cancel</Button>
                </div>
              </div>
            )}
          </div>
        )}

        {/* Tenant list */}
        {tenants.length === 0 ? (
          <div className="px-6 py-12 text-center">
            <Users size={28} className="mx-auto mb-3 opacity-20" />
            <p className="text-sm font-semibold" style={{ color: NAVY }}>No tenants on this system yet</p>
            <p className="text-xs mt-1" style={{ color: 'hsl(var(--muted-fg))' }}>
              Click "Add Tenant" to link existing MTPro tenants or create new ones.
            </p>
          </div>
        ) : (
          <div className="divide-y">
            {tenants.map((t, i) => (
              <div key={t.id} className="px-6 py-4 flex items-center gap-4 flex-wrap" data-testid={`system-tenant-${i}`}>
                <div className="w-8 h-8 rounded-full flex items-center justify-center flex-shrink-0 text-xs font-black"
                  style={{ background: PURPLE + '1a', color: PURPLE }}>
                  {(t.company_name || '?')[0].toUpperCase()}
                </div>
                <div className="flex-1 min-w-0">
                  <p className="font-semibold text-sm truncate" style={{ color: NAVY }}>{t.company_name}</p>
                  {t.email && <p className="text-xs truncate" style={{ color: 'hsl(var(--muted-fg))' }}>{t.email}</p>}
                </div>
                <UserCountEditor
                  value={t.num_users}
                  onSave={v => handleUpdateUsers(t.id, v)}
                />
                <button onClick={() => handleUnlink(t.id, t.company_name)}
                  className="p-1.5 rounded-md flex-shrink-0 transition-colors hover:bg-red-50"
                  style={{ color: 'hsl(var(--danger))' }} title="Remove from system">
                  <Trash2 size={14} />
                </button>
              </div>
            ))}
          </div>
        )}

        {/* Footer totals */}
        {tenants.length > 0 && (
          <div className="px-6 py-3 flex items-center justify-between text-xs"
            style={{ background: '#f8fafc', borderTop: '1px solid hsl(var(--border))' }}>
            <span style={{ color: 'hsl(var(--muted-fg))' }}>{tenants.length} tenant{tenants.length !== 1 ? 's' : ''}</span>
            <span className="font-bold tabular-nums" style={{ color: NAVY }}>
              {totalAllocated} / {systemUsers} users
            </span>
          </div>
        )}
      </div>
    </div>
  );
}
